import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper, useTheme } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { useUiChromeStore, BOTTOM_NAV_HEIGHT } from '../../store/uiChromeStore';

// The three primary destinations (the 4th slot is "More" -> opens the drawer
// for Spruce Inbox, Granola, Bulk Messaging, Billing, Admin).
const NAV_ITEMS = [
  { value: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { value: 'calendar', label: 'Calendar', icon: <CalendarIcon />, path: '/calendar' },
  { value: 'contacts', label: 'Contacts', icon: <PeopleIcon />, path: '/contacts' },
] as const;

// Map the current pathname to a nav value (or false = nothing highlighted, e.g.
// /billing which lives under "More"). Exact match for "/", startsWith for rest.
const valueForPath = (pathname: string): string | false => {
  for (const item of NAV_ITEMS) {
    if (item.path === '/') {
      if (pathname === '/') return item.value;
    } else if (pathname === item.path || pathname.startsWith(item.path + '/')) {
      return item.value;
    }
  }
  return false;
};

type Props = { onMore: () => void };

// Global app-level bottom navigation. Shown only on mobile (display gated at the
// same `md` breakpoint MainLayout uses for the hamburger/temporary drawer — see
// the breakpoint rule in the plan). Hidden in "immersive" mode (patient
// detail / chat) so the in-page composer + sub-tabs own the bottom.
const MobileBottomNav: React.FC<Props> = ({ onMore }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const immersive = useUiChromeStore((s) => s.immersive);

  if (immersive) return null;

  const value = valueForPath(location.pathname);

  return (
    <Paper
      elevation={8}
      sx={{
        display: { xs: 'block', md: 'none' },
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: '1px solid',
        borderColor: 'divider',
        // Honour the iOS home-indicator inset below the bar.
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <BottomNavigation
        showLabels
        value={value}
        onChange={(_, next) => {
          if (next === 'more') {
            onMore();
            return;
          }
          const item = NAV_ITEMS.find((i) => i.value === next);
          if (item) navigate(item.path);
        }}
        sx={{
          height: BOTTOM_NAV_HEIGHT,
          '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
          '& .MuiBottomNavigationAction-label': { fontSize: '0.68rem' },
        }}
      >
        {NAV_ITEMS.map((item) => (
          <BottomNavigationAction
            key={item.value}
            value={item.value}
            label={item.label}
            icon={item.icon}
          />
        ))}
        <BottomNavigationAction value="more" label="More" icon={<MoreIcon />} />
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;
